/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'

const PdfViewer = dynamic(() => import('@/src/components/pdf-viewer'), { ssr: false })

interface SocialPostMediaProps {
  type?: string
  url?: string
  alt?: string
  className?: string
  mimeType?: string
}

// Percent-encode the characters that would otherwise break either an attribute or the
// url("...") the blurred backdrop is built from
const encodeUrl = (url?: string) =>
  url?.replaceAll(' ', '%20').replaceAll('\\', '%5C').replaceAll('"', '%22') ?? ''

// The blurred backdrop only ever shows out of focus, so a thumbnail-sized snapshot of the
// rendered page is enough and keeps the data URL tiny.
const BACKDROP_WIDTH = 48

const snapshot = (canvas: HTMLCanvasElement) => {
  const target = document.createElement('canvas')
  target.width = BACKDROP_WIDTH
  target.height = Math.max(1, Math.round((canvas.height / canvas.width) * BACKDROP_WIDTH))
  const ctx = target.getContext('2d')
  if (!ctx) return ''
  ctx.drawImage(canvas, 0, 0, target.width, target.height)
  return target.toDataURL('image/jpeg', 0.6)
}

const SocialPostMedia = ({
  type = 'image',
  url,
  alt = '',
  className = '',
  mimeType
}: SocialPostMediaProps) => {
  const mediaUrl = encodeUrl(url)
  const pdfRef = useRef<HTMLDivElement>(null)
  const [pdfBackdrop, setPdfBackdrop] = useState('')

  // Returns true once the page has finished painting and there is nothing left to wait for,
  // whether or not the snapshot itself worked out.
  const capturePdfBackdrop = useCallback(() => {
    const canvas = pdfRef.current?.querySelector('canvas')
    // react-pdf keeps the canvas hidden until the page has finished painting
    if (!canvas?.width || canvas.style.visibility === 'hidden') return false
    try {
      const data = snapshot(canvas)
      if (data) setPdfBackdrop(data)
    } catch {
      // A tainted canvas just leaves the tile on its plain backdrop
    }
    return true
  }, [])

  useEffect(() => {
    setPdfBackdrop('')
    const container = pdfRef.current
    if (type !== 'pdf' || !container) return
    if (capturePdfBackdrop()) return

    const observer = new MutationObserver(() => {
      if (capturePdfBackdrop()) observer.disconnect()
    })
    observer.observe(container, { childList: true, subtree: true, attributes: true })
    return () => observer.disconnect()
  }, [type, mediaUrl, capturePdfBackdrop])

  if (!mediaUrl) return null

  const backdrop = type === 'image' ? mediaUrl : type === 'pdf' ? pdfBackdrop : ''

  return (
    <div
      className={`relative mt-2 w-full aspect-[16/9] overflow-hidden bg-[#F4F4F4] ${className}`.trim()}
    >
      {!!backdrop && (
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-center bg-cover blur-lg scale-125"
          style={{ backgroundImage: `url("${backdrop}")` }}
        />
      )}

      {type === 'image' && (
        <img src={mediaUrl} alt={alt} className="relative h-full w-full object-contain" />
      )}

      {type === 'video' && (
        <video controls className="relative h-full w-full object-contain">
          <source src={mediaUrl} type={mimeType || 'video/mp4'} />
          Your browser does not support the video tag.
        </video>
      )}

      {type === 'pdf' && (
        <div
          ref={pdfRef}
          className="social-pdf-thumb absolute inset-0 flex items-center justify-center overflow-hidden"
        >
          <PdfViewer pdfFile={mediaUrl} customPage={1} height={400} />
        </div>
      )}
    </div>
  )
}

export default SocialPostMedia
